/**
 * OCR routing layer: maps raw OCR output to a destination flow.
 *
 * All three OCR entry points (Prompts 7.4–7.6) pass their OcrResult.text
 * here and receive a routing decision. The entry points own the UI response
 * (opening the palette, showing a confirmation sheet, etc.); this module
 * owns only the classification logic.
 *
 * Routes:
 *   'quick-lookup' — short text (word / phrase), send to QuickLookupPalette
 *   'text-import'  — longer text (sentence / paragraph), offer text-import flow
 *   'empty'        — nothing usable was extracted; surface "couldn't read" state
 *
 * text-import constraints:
 *   The authenticated /text-import/extract/ endpoint accepts up to 10 000
 *   characters. The text-import page enforces this on submit — the router
 *   does not truncate, but callers can check OcrRoutingDecision.text.length
 *   if they need to warn before navigation.
 */

// ── Threshold & noise patterns ────────────────────────────────────────────────

/**
 * Normalized character count below which OCR output is routed to Quick Lookup.
 * At or above this count the text-import flow is offered instead.
 * Tune this constant; it is the only place the threshold is defined.
 */
export const OCR_ROUTE_THRESHOLD = 50;

/** Control characters that are OCR garbage (everything non-printable except whitespace). */
// eslint-disable-next-line no-control-regex -- matching control characters is this regex's purpose
const GARBAGE_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g;

// ── Normalisation helpers (not exported — routing detail) ─────────────────────

function stripGarbage(s: string): string {
  return s.replace(GARBAGE_RE, '');
}

/**
 * Fully collapses all whitespace to single spaces.
 * Used for: length measurement and the quick-lookup text value.
 * "hello\n\n" → "hello" (5 chars, not 8).
 */
function collapseWhitespace(s: string): string {
  return stripGarbage(s).replace(/\s+/g, ' ').trim();
}

/**
 * Preserves paragraph structure while cleaning garbage and redundant whitespace.
 * Used for the text-import text value so the textarea retains the page layout.
 */
function normalizeForImport(s: string): string {
  return stripGarbage(s)
    .replace(/[^\S\n]+/g, ' ')   // collapse non-newline whitespace runs
    .replace(/\n{3,}/g, '\n\n')  // cap consecutive blank lines at one
    .trim();
}

// ── Public types ──────────────────────────────────────────────────────────────

/** Routing outcome returned to an OCR entry point. */
export type OcrRoutingDecision =
  | {
      kind: 'quick-lookup';
      /** Fully normalised single-line text suitable for QuickLookupPalette initialQuery. */
      text: string;
    }
  | {
      kind: 'text-import';
      /**
       * Paragraph-structure-preserved text ready for TextImportPage prefillText.
       * Navigate with: navigate('/text-import', { state: { prefillText: text } })
       * The long-text branch requires a user confirmation step before navigation —
       * the entry point must not auto-submit.
       */
      text: string;
    }
  | {
      kind: 'empty';
      /** No usable text was found; surface "couldn't read this image" to the user. */
    };

// ── Router ────────────────────────────────────────────────────────────────────

/**
 * Route raw OCR text to the appropriate destination.
 *
 * @param rawText  OcrResult.text as returned by recognize()
 * @returns        Routing decision — see OcrRoutingDecision for each branch
 */
export function routeOcrResult(rawText: string): OcrRoutingDecision {
  const measured = collapseWhitespace(rawText);

  if (measured.length === 0) {
    return { kind: 'empty' };
  }

  if (measured.length < OCR_ROUTE_THRESHOLD) {
    return { kind: 'quick-lookup', text: measured };
  }

  return { kind: 'text-import', text: normalizeForImport(rawText) };
}
